import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { logAmplitudeEvent } from '../../../../../utils'
import { MentionableItem } from '../../../../../types'
import type { MentionableChangeEvent, MentionableSelectionEvent } from '../../../../elements/MentionableInput'
import {
  closeMentionsAtom,
  cursorPositionAtom,
  mentionSearchAtom,
  openMentionsAtom,
  showMentionsAtom,
  slashModeAtom,
  useMessageMentions,
  useSetMessageMentions,
} from '../atoms'

export const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const useValidateMentions = () => {
  return useCallback((message: string, mentions: Array<MentionableItem>) => {
    if (!mentions.length) {
      return []
    }
    return mentions.filter((mention) => {
      if (message.includes(`@${mention.label}`)) return true
      // Slash mentions only count when `/` is at the start of the message or preceded by whitespace,
      // so URLs/file paths containing the label don't falsely validate. The trailing `\b` (word
      // boundary) — not `(?:\s|$)` — mirrors the editor's pill-rendering regex exactly, so a skill
      // stays selected when followed by punctuation (e.g. `/Summarize:`), instead of the validation
      // dropping it and reverting the pill to plain text while the user is still typing.
      const slashPattern = new RegExp(`(?:^|\\s)/${escapeRegex(mention.label)}\\b`)
      return slashPattern.test(message)
    })
  }, [])
}

/** Closes the menu and drops the mentions already attached to the draft. */
export const useResetMentions = () => {
  const setMentions = useSetMessageMentions()
  const closeMentions = useSetAtom(closeMentionsAtom)
  const setCursorPosition = useSetAtom(cursorPositionAtom)
  return useCallback(() => {
    closeMentions()
    setMentions([])
    setCursorPosition(0)
  }, [closeMentions, setMentions, setCursorPosition])
}

/** Drives the menu off what the user types: `@` opens it, `/` opens skills-only, typing filters. */
export const useMentionsHandler = () => {
  const messageMentions = useMessageMentions()
  const setMentionSearch = useSetAtom(mentionSearchAtom)
  const showMentions = useAtomValue(showMentionsAtom)
  const setCursorPosition = useSetAtom(cursorPositionAtom)
  const setMentions = useSetMessageMentions()
  const validateMentions = useValidateMentions()
  const slashMode = useAtomValue(slashModeAtom)
  const openMentions = useSetAtom(openMentionsAtom)
  const closeMentions = useSetAtom(closeMentionsAtom)

  const selectMention = useCallback((mention: MentionableItem) => {
    closeMentions()
    setCursorPosition(0)
    setMentions((prev) => [...prev, mention])
  }, [])

  const onSelectionChangeHandler = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement> | MentionableSelectionEvent) => {
      const target = e.target as HTMLTextAreaElement
      setCursorPosition(target.selectionStart)
    },
    [],
  )

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement> | MentionableChangeEvent) => {
      const value = e.target.value

      // Limit to 1 mention per message for now
      const validatedMentions = validateMentions(value, messageMentions)
      if (validatedMentions.length === 0 && messageMentions.length > 0) {
        setMentions([])
      }
      const cursorPos = Math.min(e.target.selectionStart, value.length)
      const charBeforeCursor = value[cursorPos - 1]

      const charBeforeSlash = value[cursorPos - 2]
      const slashAtValidPosition =
        charBeforeCursor === '/' && (cursorPos === 1 || !charBeforeSlash || /\s/.test(charBeforeSlash))

      if (charBeforeCursor === '@') {
        openMentions({ slashMode: false })
        logAmplitudeEvent('Mentions', {
          action: 'keyboard open',
          source: window.__RENDERER_NAME,
        })
      } else if (slashAtValidPosition) {
        openMentions({ slashMode: true })
        logAmplitudeEvent('Mentions', {
          action: 'slash open',
          source: window.__RENDERER_NAME,
        })
      } else if (showMentions) {
        const triggerChar = slashMode ? '/' : '@'
        if (value.indexOf(triggerChar) === -1) {
          closeMentions()
          return
        }
        const mentionStart = value.slice(0, cursorPos).lastIndexOf(triggerChar)
        const mentionQuery = mentionStart !== -1 ? value.slice(mentionStart + 1, cursorPos) : ''
        if (mentionQuery === '') {
          closeMentions()
          return
        }
        setMentionSearch(mentionQuery)
      }
    },
    [showMentions, slashMode, messageMentions, validateMentions, openMentions, closeMentions, setMentionSearch],
  )

  return {
    selectMention,
    onChangeHandler,
    onSelectionChangeHandler,
  }
}
